import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthContext'

const API_BASE = import.meta.env.DEV ? '/api' : 'https://asia-northeast1-bluepaper2.cloudfunctions.net/api'

interface PlanLimits {
  maxMembers: number
  maxAIFormats: number
  botHoursPerSeat: number
  orgPoolHours: number
  retentionDays: number
  minSeats: number
}

interface PlanDef {
  id: string
  name: string
  nameEn: string
  price: number
  priceMonthly: number
  limits: PlanLimits
  features: string[]
  selfServe: boolean
}

interface PoolInfo {
  unlimited: boolean
  poolMinutes: number
  usedMinutes: number
  remainingMinutes: number
}

interface PlanInfo {
  current: { type: string; billing: string; seats: number; contractSeats?: number | null; pool?: PoolInfo | null }
  stripeConfigured?: boolean
  definitions: PlanDef[]
}

function poolLabel(p: PlanDef, seats: number): string {
  if (p.limits.botHoursPerSeat === -1) return 'bot会議 無制限'
  if (p.limits.botHoursPerSeat === 0) return `bot会議 組織全体で月${p.limits.orgPoolHours}時間まで`
  const effective = Math.max(seats, p.limits.minSeats)
  return `bot会議 月${effective * p.limits.botHoursPerSeat}時間まで（${p.limits.botHoursPerSeat}時間×${effective}人・組織で共有）`
}


/** 比較表の行定義（表示専用。functions/src/config/plans.ts と同期を保つこと） */
const PLAN_MATRIX: { label: string; values: Record<string, string> }[] = [
  { label: '最低利用人数', values: { free: '1人（3人まで）', standard: '2人〜', business: '3人〜', business_plus: '5人〜', enterprise: '—' } },
  { label: '閲覧者（viewer）無料', values: { free: '○', standard: '○', business: '○', business_plus: '○', enterprise: '○' } },
  { label: 'bot会議の利用可能時間/月', values: { free: '組織全体で3時間', standard: '8時間×人数', business: '20時間×人数', business_plus: '60時間×人数', enterprise: '無制限' } },
  { label: '音声アップロード', values: { free: '月5ファイル', standard: '録音1時間=0.5時間分を消費', business: '同左', business_plus: '同左', enterprise: '無制限' } },
  { label: 'AI整形（テキスト由来）', values: { free: '月50回', standard: '100回×人数', business: '無制限', business_plus: '無制限', enterprise: '無制限' } },
  { label: '限定メンバーフォルダ（ACL）', values: { free: '✕', standard: '○', business: '○', business_plus: '○', enterprise: '○' } },
  { label: '組織デフォルト共有設定', values: { free: '✕', standard: '○', business: '○', business_plus: '○', enterprise: '○' } },
  { label: 'Slack/メール通知・Circleback連携', values: { free: '✕', standard: '○', business: '○', business_plus: '○', enterprise: '○' } },
  { label: '利用状況レポート', values: { free: '✕', standard: '合計のみ', business: 'メンバー別', business_plus: 'メンバー別', enterprise: '○' } },
  { label: '録画保持', values: { free: '30日', standard: '90日', business: '1年', business_plus: '1年', enterprise: '無制限' } },
  { label: '追加時間パック（10h ¥3,000）', values: { free: '✕', standard: '○', business: '○', business_plus: '○', enterprise: '—' } },
  { label: 'サポート', values: { free: 'コミュニティ', standard: 'メール', business: '優先（1営業日）', business_plus: '優先（1営業日）', enterprise: '専任' } }
]

/** 設定＞組織管理＞プラン・請求 */
export function PlanBillingSection() {
  const { user, firebaseUser } = useAuth()
  const isAdmin = user?.role === 'admin'
  const [info, setInfo] = useState<PlanInfo | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [couponCode, setCouponCode] = useState('')
  const [couponBusy, setCouponBusy] = useState(false)
  const [packBusy, setPackBusy] = useState(false)
  const [interval, setInterval] = useState<'year' | 'month'>('year')

  // Checkoutからの戻り（?billing=success|pack_success|cancelled）
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const billing = params.get('billing')
    if (!billing) return
    if (billing === 'success') setMessage('お支払いが完了しました。プランを有効化しています（反映まで数秒かかることがあります）')
    if (billing === 'pack_success') setMessage('追加時間パックのお支払いが完了しました（今月の枠に10時間を追加）')
    if (billing === 'cancelled') setError('お支払いはキャンセルされました')
    params.delete('billing')
    const rest = params.toString()
    window.history.replaceState(null, '', window.location.pathname + (rest ? `?${rest}` : ''))
    // webhook反映待ちを考慮して少し後に再取得
    setTimeout(() => { fetchPlan() }, 4000)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const authFetch = useCallback(async (path: string, init?: RequestInit) => {
    if (!firebaseUser || !user?.orgId) throw new Error('未認証です')
    const token = await firebaseUser.getIdToken()
    return fetch(`${API_BASE}/v1/organizations/${user.orgId}${path}`, {
      ...init,
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        ...(init?.headers || {})
      }
    })
  }, [firebaseUser, user?.orgId])

  const fetchPlan = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await authFetch('/plan')
      if (!res.ok) throw new Error(`プラン情報の取得に失敗しました (${res.status})`)
      setInfo(await res.json())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'プラン情報の取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [authFetch])

  useEffect(() => {
    if (firebaseUser && user?.orgId) fetchPlan()
  }, [fetchPlan, firebaseUser, user?.orgId])

  const changePlan = async (type: string, name: string) => {
    const def = info?.definitions.find((d) => d.id === type)
    const isPaid = !!def && def.price > 0
    setSaving(type)
    setError(null)
    setMessage(null)
    try {
      if (isPaid && info?.stripeConfigured) {
        // Stripe Checkoutへ（席数×選択した支払いサイクル）
        const res = await authFetch('/billing/checkout', {
          method: 'POST',
          body: JSON.stringify({ planType: type, interval })
        })
        const data = await res.json().catch(() => ({}))
        if (res.status === 503) throw new Error('決済の設定が未完了です。運営にお問い合わせください。')
        if (!res.ok) throw new Error(data.error || `決済ページの作成に失敗しました (${res.status})`)
        window.location.href = data.url
        return
      }
      // 無料プランへの変更（または決済未設定時のフォールバック）
      if (!window.confirm(`プランを「${name}」に変更しますか？`)) { setSaving(null); return }
      const res = await authFetch('/plan', { method: 'PUT', body: JSON.stringify({ type }) })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `プランの変更に失敗しました (${res.status})`)
      setMessage(`プランを「${name}」に変更しました`)
      await fetchPlan()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'プランの変更に失敗しました')
    } finally {
      setSaving(null)
    }
  }

  const openPortal = async () => {
    setError(null)
    try {
      const res = await authFetch('/billing/portal', { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `請求管理ページを開けませんでした (${res.status})`)
      window.location.href = data.url
    } catch (e) {
      setError(e instanceof Error ? e.message : '請求管理ページを開けませんでした')
    }
  }

  const redeemCoupon = async () => {
    if (!couponCode.trim()) return
    setCouponBusy(true)
    setError(null)
    setMessage(null)
    try {
      if (!firebaseUser) throw new Error('未認証です')
      const token = await firebaseUser.getIdToken()
      const res = await fetch(`${API_BASE}/v1/coupons/redeem`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: couponCode.trim() })
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `クーポンの適用に失敗しました (${res.status})`)
      setMessage('クーポンを適用しました')
      setCouponCode('')
      await fetchPlan()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'クーポンの適用に失敗しました')
    } finally {
      setCouponBusy(false)
    }
  }

  const buyExtraPack = async () => {
    setPackBusy(true)
    setError(null)
    setMessage(null)
    try {
      if (info?.stripeConfigured) {
        const res = await authFetch('/billing/extra-pack-checkout', { method: 'POST' })
        const data = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(data.error || `決済ページの作成に失敗しました (${res.status})`)
        window.location.href = data.url
        return
      }
      if (!window.confirm('追加時間パック（bot会議10時間 / ¥3,000）を追加しますか？\n（決済未設定のため請求は発生しません）')) { setPackBusy(false); return }
      const res = await authFetch('/plan/extra-pack', { method: 'POST' })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(data.error || `購入に失敗しました (${res.status})`)
      setMessage(`追加パックを追加しました（今月の追加枠: ${data.extraBotHours}時間）`)
    } catch (e) {
      setError(e instanceof Error ? e.message : '購入に失敗しました')
    } finally {
      setPackBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2" style={{ padding: 32 }}>
        <span className="spinner" />
        <span className="text-secondary text-sm">読み込み中...</span>
      </div>
    )
  }

  const current = info?.current
  const currentDef = info?.definitions.find((d) => d.id === current?.type)

  return (
    <div className="flex flex-col gap-4">
      {/* 決済未接続時のみの案内 */}
      {info && !info.stripeConfigured && (
        <div className="alert warning">
          <div className="alert-body">
            <p className="text-sm">
              <strong>決済機能（クレジットカード決済）は現在準備中です。</strong>
              プラン変更・追加時間パックの操作は無償トライアルとして即時反映され、請求は発生しません。
            </p>
          </div>
        </div>
      )}

      {error && <div className="alert danger">{error}</div>}
      {message && <div className="alert success">{message}</div>}

      {/* 現在のプラン */}
      <div className="card">
        <div className="card-header">
          <div>
            <h2 className="text-lg font-semibold">現在のプラン</h2>
            <p className="form-section-desc">課金対象ユーザー（編集者以上）: {current?.seats ?? '-'}人 ・ 閲覧のみのユーザーは無料・無制限</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="badge badge-info">{currentDef?.name || current?.type}</span>
            {current?.billing === 'trial' && <span className="badge badge-warning">トライアル（決済未設定）</span>}
          </div>
        </div>
        {currentDef && (
          <div className="text-sm text-secondary">
            {poolLabel(currentDef, current?.seats || 0)}
            {' ・ '}録画保持{currentDef.limits.retentionDays === -1 ? '無制限' : `${currentDef.limits.retentionDays}日`}
          </div>
        )}

        {/* 今月のbot利用状況バー */}
        {current?.pool && !current.pool.unlimited && current.pool.poolMinutes > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className="flex items-center justify-between text-xs text-secondary" style={{ marginBottom: 4 }}>
              <span>今月のbot会議時間</span>
              <span className="tabular-nums">
                {(current.pool.usedMinutes / 60).toFixed(1)} / {Math.floor(current.pool.poolMinutes / 60)}時間
              </span>
            </div>
            <div style={{ height: 8, borderRadius: 999, background: 'var(--color-surface, #eef1f7)', overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${Math.min(100, Math.round((current.pool.usedMinutes / current.pool.poolMinutes) * 100))}%`,
                  background: current.pool.usedMinutes / current.pool.poolMinutes >= 0.8 ? 'var(--color-warning-mid, #d97706)' : 'var(--color-accent, #3b5bdb)',
                  transition: 'width .3s'
                }}
              />
            </div>
            {current.pool.remainingMinutes <= 0 && (
              <p className="text-xs" style={{ color: 'var(--color-danger, #dc2626)', marginTop: 4 }}>
                上限に達しています。新しいbot派遣はブロックされます（追加パック購入で回復）。
              </p>
            )}
          </div>
        )}

        {/* 契約席数と実メンバー数のズレ表示（Stripe契約中のみ） */}
        {current?.billing === 'stripe' && typeof current.contractSeats === 'number' && (
          <div className="flex items-center gap-3 text-sm text-secondary" style={{ marginTop: 12 }}>
            <span>
              契約中の席数: <strong>{current.contractSeats}席</strong>
              {' ／ '}現在の編集者以上: <strong>{current.seats}人</strong>
            </span>
            {current.contractSeats !== Math.max(current.seats, currentDef?.limits.minSeats || 1) && isAdmin && (
              <button
                className="btn btn-outline btn-sm"
                onClick={async () => {
                  setError(null)
                  try {
                    const res = await authFetch('/billing/sync-seats', { method: 'POST' })
                    const data = await res.json().catch(() => ({}))
                    if (!res.ok) throw new Error(data.error || '同期に失敗しました')
                    setMessage(`契約席数を${data.quantity}席に更新しました（増席は日割り請求されます）`)
                    await fetchPlan()
                  } catch (e) {
                    setError(e instanceof Error ? e.message : '同期に失敗しました')
                  }
                }}
              >
                契約席数を人数に合わせる
              </button>
            )}
          </div>
        )}
        {isAdmin && (
          <div className="flex items-center gap-3 flex-wrap" style={{ marginTop: 12, borderTop: '1px solid var(--color-border-light)', paddingTop: 12 }}>
            {/* 追加時間パック（無料プランは対象外） */}
            {current?.type !== 'free' && current?.type !== 'enterprise' && (
              <button className="btn btn-outline btn-sm" disabled={packBusy} onClick={buyExtraPack}>
                {packBusy ? <span className="spinner sm" /> : '追加時間パックを購入（10h ¥3,000）'}
              </button>
            )}
            {/* Stripe契約中は請求管理（カード変更・解約・領収書） */}
            {current?.billing === 'stripe' && (
              <button className="btn btn-outline btn-sm" onClick={openPortal}>
                支払い・請求の管理
              </button>
            )}
            {/* クーポンコード */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                className="form-input"
                style={{ width: 180, textTransform: 'uppercase' }}
                placeholder="クーポンコード"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
              />
              <button className="btn btn-outline btn-sm" disabled={couponBusy || !couponCode.trim()} onClick={redeemCoupon}>
                {couponBusy ? <span className="spinner sm" /> : '適用'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 支払いサイクル */}
      {(
        <div className="flex items-center gap-2">
          <span className="text-sm text-secondary">支払いサイクル:</span>
          <div className="tab-list" style={{ marginBottom: 0, borderBottom: 'none' }}>
            <button className={`tab-item${interval === 'year' ? ' active' : ''}`} onClick={() => setInterval('year')}>
              年払い（約20%お得）
            </button>
            <button className={`tab-item${interval === 'month' ? ' active' : ''}`} onClick={() => setInterval('month')}>
              月払い
            </button>
          </div>
        </div>
      )}

      {/* プラン比較（機能×プランの◯✕表） */}
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div className="table-container" style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', minWidth: 720 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', minWidth: 160 }}>機能</th>
                {info?.definitions.map((p) => (
                  <th key={p.id} style={{ textAlign: 'center', ...(p.id === current?.type ? { background: 'var(--color-accent-light, #eef1f7)' } : {}) }}>
                    <div className="text-sm font-semibold">{p.name}</div>
                    {p.id === current?.type && <span className="badge badge-info" style={{ marginTop: 2 }}>利用中</span>}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* 価格行（選択中の支払いサイクルに連動） */}
              <tr>
                <td className="text-sm" style={{ fontWeight: 600 }}>
                  月額（1ユーザーあたり）
                  <div className="text-xs text-tertiary" style={{ fontWeight: 400 }}>
                    {interval === 'year' ? '年払い（12ヶ月分を一括請求）' : '月払い'}
                  </div>
                </td>
                {info?.definitions.map((p) => {
                  const style: React.CSSProperties = {
                    textAlign: 'center',
                    ...(p.id === current?.type ? { background: 'var(--color-accent-light, #eef1f7)' } : {})
                  }
                  if (!p.selfServe) return <td key={p.id} className="text-sm" style={style}>個別見積</td>
                  if (p.price === 0) return <td key={p.id} className="text-sm" style={style}>¥0</td>
                  const shown = interval === 'year' ? p.price : p.priceMonthly
                  const other = interval === 'year' ? p.priceMonthly : p.price
                  return (
                    <td key={p.id} style={style}>
                      <div className="text-sm font-semibold">¥{shown.toLocaleString()}</div>
                      <div className="text-xs text-tertiary">
                        {interval === 'year'
                          ? `年間 ¥${(p.price * 12).toLocaleString()}（月払いなら¥${other.toLocaleString()}）`
                          : `年払いなら¥${other.toLocaleString()}/月`}
                      </div>
                    </td>
                  )
                })}
              </tr>
              {PLAN_MATRIX.map((row) => (
                <tr key={row.label}>
                  <td className="text-sm" style={{ fontWeight: 500 }}>{row.label}</td>
                  {info?.definitions.map((p) => {
                    const v = row.values[p.id] ?? '—'
                    const style: React.CSSProperties = {
                      textAlign: 'center',
                      ...(p.id === current?.type ? { background: 'var(--color-accent-light, #eef1f7)' } : {})
                    }
                    if (v === '○') return <td key={p.id} style={{ ...style, color: 'var(--color-success-mid, #059669)', fontWeight: 700 }}>○</td>
                    if (v === '✕') return <td key={p.id} style={{ ...style, color: 'var(--color-text-tertiary, #9ca3af)' }}>✕</td>
                    return <td key={p.id} className="text-sm" style={style}>{v}</td>
                  })}
                </tr>
              ))}
              {/* 変更アクション行 */}
              <tr>
                <td />
                {info?.definitions.map((p) => {
                  const isCurrent = p.id === current?.type
                  return (
                    <td key={p.id} style={{ textAlign: 'center', padding: '12px 8px', ...(isCurrent ? { background: 'var(--color-accent-light, #eef1f7)' } : {}) }}>
                      {isCurrent ? (
                        <span className="text-xs text-secondary">現在のプラン</span>
                      ) : !isAdmin ? null : p.selfServe ? (
                        <button
                          className="btn btn-primary btn-sm"
                          disabled={saving !== null}
                          onClick={() => changePlan(p.id, p.name)}
                        >
                          {saving === p.id ? <span className="spinner sm" /> : '変更'}
                        </button>
                      ) : (
                        <a className="btn btn-outline btn-sm" href="mailto:support@bp-g.jp?subject=BluePaper%20Enterprise%E3%81%AE%E5%95%8F%E3%81%84%E5%90%88%E3%82%8F%E3%81%9B">
                          問い合わせ
                        </a>
                      )}
                    </td>
                  )
                })}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <p className="form-hint">
        ※ プランの変更・解約・カード変更・領収書は「支払い・請求の管理」から行えます。操作は管理者のみ可能です。
        有料プランの席数は課金対象ユーザー（編集者以上）の人数で計算されます。
      </p>
    </div>
  )
}
